import axios from 'axios';
import './page-tour.css';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useActiveProfile } from '../utils/profile';
import '../styles/milestone.css';

const library = [
  ['.profile-fav-star', 'Your favourites', 'Love a book? Save it with the star so it is easy to find again.'],
  ['.profile-playlist-btn', 'Your playlist', 'Build a listening list with the headphones button.'],
  ['.profile-card-thumb', 'About the book', 'Curious? Tap a cover for a quick look at the story.'],
  ['.card-action-read', 'Start reading', 'Ready to begin? Read opens the book. Quiz lets you try its questions.'],
  ['.tagbar-tag', 'Find your next book', 'Pick a category to discover your next story.'],
  ['.theme-toggle', 'Day and night', 'Choose a cosy dark view or a bright light one. We will remember your choice.'],
  ['.site-nav-avatar', 'Your profile and notifications', 'Your avatar takes you to My Shelf and lets you switch readers.'],
];
const reader = [
  ['.nav-next', 'Turn the page', 'Follow the arrows or swipe to turn the page.'],
  ['.intro-audio-btn, .page-listen-btn', 'Listen to the story', 'Let the story come to life. Press play to listen, and again to stop.'],
  ['.lang-toggle, .intro-lang-btn, .page-intro button[aria-label^="Switch to"]', 'Choose a language', 'Read and listen in English or Arabic.'],
  ['.orientation-toggle', 'Page layout', 'Make yourself comfortable with one page or an open-book view.'],
  ['.reader-utility', 'Make room for reading', 'Open full screen for a little more room to read.'],
  ['.back-cover-quiz-btn, .page-back', 'Finish with a quiz and rating', 'When you finish, try the quiz if this book has one. You can also rate the story after reading.'],
];
const profiles = [['.sp-grid, .sp-page', 'Who is reading?', 'Who is joining the adventure? Choose a reader or add someone new.']];
const shelf = [
  ['.profile-avatar-btn', 'Make it yours', 'Choose an avatar that feels like you. You can edit your name here too.'],
  ['.profile-noor-pill', 'Noor Points and achievements', 'Every story is a step forward. See your Noor Points and achievements here.'],
  ['.profile-tabs', 'Everything on My Shelf', 'Your stories, favourites and progress, all in one place.'],
  ['[data-guide=playlist-tab]', 'Your playlist', 'Your listening list lives here. Pick a story and settle in.'],
  ['.profile-avatar-notif-badge', 'Your notifications', 'A little update just for this reader. Open the bell to catch up.'],
];
// A control inside a carousel can sit outside the visible part of its clipping parent;
// pointing at it would highlight empty space where the item is not actually shown.
function isClipped(el, r) {
  const cx = (r.left + r.right) / 2, cy = (r.top + r.bottom) / 2;
  for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (!/(hidden|clip|auto|scroll)/.test(style.overflowX + style.overflowY)) continue;
    const box = parent.getBoundingClientRect();
    if (cx < box.left || cx > box.right || cy < box.top || cy > box.bottom) return true;
  }
  return false;
}
function findTarget(selector) {
  const candidates = [...document.querySelectorAll(selector)].filter(el => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[aria-hidden="true"]') && !isClipped(el, r);
  });
  return candidates.find(el => { const r = el.getBoundingClientRect(); return r.top >= 0 && r.bottom <= window.innerHeight && r.left >= 0 && r.right <= window.innerWidth; }) || candidates[0];
}
const clamp = (n, min, max) => Math.max(min, Math.min(n, max));

function readSession() {
  try { return { id: JSON.parse(localStorage.getItem('adeeb-user') || 'null')?._id, token: localStorage.getItem('adeeb-token') }; } catch { return {}; }
}
export default function GuidedHelp() {
  const { pathname } = useLocation();
  const profile = useActiveProfile();
  const section = pathname.startsWith('/read/') ? 'reader' : pathname === '/profile' ? 'shelf' : pathname.startsWith('/profiles/') ? 'profiles' : pathname === '/' || pathname.startsWith('/category/') ? 'library' : null;
  const steps = section === 'reader' ? reader : section === 'shelf' ? shelf : section === 'profiles' ? profiles : library;
  const [session, setSession] = useState(readSession);
  const profileId = profile?._id;
  const key = session.id && profileId ? `deenlit-profile-tour-seen:${session.id}:${profileId}${section === 'reader' ? ':reader' : ''}` : null;
  const [entryHost, setEntryHost] = useState(null);
  const [index, setIndex] = useState(-1);
  const [layout, setLayout] = useState(null);
  const card = useRef(null);
  const launcher = useRef(null);
  const stepDirection = useRef(1);
  useEffect(() => {
    const refresh = () => setSession(previous => { const next = readSession(); return previous.id === next.id && previous.token === next.token ? previous : next; });
    window.addEventListener('adeeb-auth-change', refresh); window.addEventListener('storage', refresh);
    return () => { window.removeEventListener('adeeb-auth-change', refresh); window.removeEventListener('storage', refresh); };
  }, []);
  useEffect(() => {
    const locate = () => setEntryHost(document.querySelector(section === 'reader' ? '.reader-utility' : section === 'profiles' ? '.page-tour-host' : '.page-tour-host'));
    locate(); const observer = new MutationObserver(locate); observer.observe(document.body, { childList:true, subtree:true });
    return () => observer.disconnect();
  }, [pathname, section]);
  useEffect(() => {
    setIndex(-1);
    if (!section || section === 'profiles' || !key || !session.token) return;
    let alive = true, timer, tries = 0;
    const firstVisit = async () => {
      try {
        if (localStorage.getItem(key)) return;
        if (!findTarget(steps[0][0])) { if (++tries < 15) timer = setTimeout(firstVisit, 500); return; }
        const { data } = await axios.post('/api/tour/start', { section }, { headers: { Authorization: `Bearer ${session.token}`, 'X-Profile-Id': profileId } });
        const alreadyOpened = localStorage.getItem(key);
        localStorage.setItem(key, 'seen');
        if (alive && data.firstVisit && !alreadyOpened && readSession().token === session.token) setIndex(0);
      } catch { /* Keep the manual tour available when offline. */ }
    };
    timer = setTimeout(firstVisit, 1400);
    return () => { alive = false; clearTimeout(timer); };
  }, [key, profileId, section, session.token, steps]);
  const open = () => {
    stepDirection.current = 1;
    if (key) { try { localStorage.setItem(key, 'seen'); } catch {} }
    if (session.token && profileId) axios.post('/api/tour/start', { section }, { headers: { Authorization: `Bearer ${session.token}`, 'X-Profile-Id': profileId } }).catch(() => {});
    setIndex(0);
  };
  useEffect(() => {
    if (index < 0) { setLayout(null); return; }
    let scrolledTarget = null, highlighted = null, frame;
    let openedMenu = false;
    const selector = steps[index][0];
    const update = () => {
      let target = findTarget(selector), hint = '';
      // Reveal navigation controls that live inside the mobile menu.
      if (!target && /theme-toggle|site-nav-avatar/.test(selector)) {
        const menu = document.querySelector('button[aria-label="Toggle menu"]');
        if (menu && menu.getBoundingClientRect().width && menu.getAttribute('aria-expanded') === 'false') {
          menu.click(); openedMenu = true;
        }
        if (!profile?._id && selector.includes('site-nav-avatar')) {
          target = findTarget('a[href="/login"]');
          hint = 'Sign in here first to choose a reader and see their notifications.';
        }
      }
      // Ask the reader to reveal the real control before positioning the tip.
      if (!target && section === 'reader') {
        const detail = { selector };
        window.dispatchEvent(new CustomEvent('deenlit-reader-tour-reveal', { detail }));
        if (detail.unavailable) { setIndex(i => { const next = i + stepDirection.current; return next >= 0 && next < steps.length ? next : -1; }); return; }
        hint = 'Opening the page for this tip?';
      }
      if (highlighted !== target) { highlighted?.classList.remove('tour-target'); highlighted = target; target?.classList.add('tour-target'); }
      if (target && target !== scrolledTarget) {
        scrolledTarget = target;
        const r = target.getBoundingClientRect();
        const top = target.closest('.site-nav') ? 8 : 100;
        if (r.top < top || r.bottom > window.innerHeight - 12 || r.left < 0 || r.right > window.innerWidth) target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' });
      }
      const w = Math.min(340, window.innerWidth - 24);
      const h = Math.min(card.current?.offsetHeight || 250, window.innerHeight - 24);
      let left = (window.innerWidth - w) / 2, top = window.innerHeight - h - 12, rect = null, line = null;
      if (target) {
        const r = target.getBoundingClientRect();
        rect = { left: clamp(r.left - 5, 3, window.innerWidth - 3), top: clamp(r.top - 5, 3, window.innerHeight - 3), right: clamp(r.right + 5, 3, window.innerWidth - 3), bottom: clamp(r.bottom + 5, 3, window.innerHeight - 3) };
        const cx = (rect.left + rect.right) / 2, cy = (rect.top + rect.bottom) / 2;
        let side;
        if (rect.right + w + 32 <= window.innerWidth) { side = 'right'; left = rect.right + 24; top = clamp(cy - h / 2, 12, window.innerHeight - h - 12); }
        else if (rect.left - w - 32 >= 0) { side = 'left'; left = rect.left - w - 24; top = clamp(cy - h / 2, 12, window.innerHeight - h - 12); }
        else if (rect.bottom + h + 32 <= window.innerHeight) { side = 'bottom'; left = clamp(cx - w / 2, 12, window.innerWidth - w - 12); top = rect.bottom + 24; }
        else { side = 'top'; left = clamp(cx - w / 2, 12, window.innerWidth - w - 12); top = Math.max(12, rect.top - h - 24); }
        if (side === 'right') line = { x1:left, y1:clamp(cy,top+16,top+h-16), x2:rect.right+2, y2:cy };
        if (side === 'left') line = { x1:left+w, y1:clamp(cy,top+16,top+h-16), x2:rect.left-2, y2:cy };
        if (side === 'bottom') line = { x1:clamp(cx,left+16,left+w-16), y1:top, x2:cx, y2:rect.bottom+2 };
        if (side === 'top') line = { x1:clamp(cx,left+16,left+w-16), y1:top+h, x2:cx, y2:rect.top-2 };
      }
      const next = { left, top, width:w, rect, line, hint, target: target?.className || '' };
      setLayout(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    setLayout(null); update(); card.current?.focus({ preventScroll: true });
    const timer = setInterval(update, 200);
    window.addEventListener('scroll', schedule, true); window.addEventListener('resize', schedule);
    const resize = new ResizeObserver(schedule); if (card.current) resize.observe(card.current);
    return () => {
      clearInterval(timer); cancelAnimationFrame(frame); resize.disconnect(); highlighted?.classList.remove('tour-target');
      window.removeEventListener('scroll', schedule, true); window.removeEventListener('resize', schedule);
      if (openedMenu) { const menu = document.querySelector('button[aria-label="Toggle menu"]'); if (menu?.getAttribute('aria-expanded') === 'true') menu.click(); }
    };
  }, [index, steps, section, profile?._id]);
  const tourOpen = index >= 0;
  // Moving carousels would slide the highlighted control away from its tip, so they pause while a tour is open.
  useEffect(() => {
    if (!tourOpen) return;
    document.documentElement.classList.add('tour-open');
    return () => document.documentElement.classList.remove('tour-open');
  }, [tourOpen]);
  useEffect(() => {
    if (!tourOpen || section !== 'reader') return;
    window.dispatchEvent(new Event('deenlit-reader-tour-start'));
    return () => window.dispatchEvent(new Event('deenlit-reader-tour-end'));
  }, [tourOpen, section]);
  const positioned = Boolean(layout);
  useEffect(() => { if (index >= 0 && positioned) card.current?.focus({ preventScroll: true }); }, [index, positioned]);
  const close = () => { setIndex(-1); launcher.current?.focus({ preventScroll: true }); };
  if (!section) return null;
  return <>
    {entryHost && createPortal(<button ref={launcher} className="page-tour-link" onClick={open} aria-haspopup="dialog" aria-label="Page tour" title="Page tour"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m16 8-2 6-6 2 2-6Z"/></svg></button>, entryHost)}
    {index >= 0 && createPortal(<div data-guide-ui="true">
      {layout?.rect && <div className="tour-spotlight" aria-hidden="true" style={{ left:layout.rect.left, top:layout.rect.top, width:layout.rect.right-layout.rect.left, height:layout.rect.bottom-layout.rect.top }} />}
      {layout?.line && <svg className="tour-pointer" aria-hidden="true"><defs><marker id="guide-arrowhead" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 Z" fill="#ffce54" /></marker></defs><line {...layout.line} stroke="#ffce54" strokeWidth="3" markerEnd="url(#guide-arrowhead)" /></svg>}
      <aside ref={card} tabIndex={-1} className="tour-card tour-card-anchored page-tour-card" role="dialog" aria-label="Reading guide" aria-describedby="guide-description" style={layout ? { left:layout.left, top:layout.top, width:layout.width } : { visibility:'hidden' }} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
        <div className="page-tour-head"><span className="page-tour-kicker">A LITTLE GUIDANCE</span><button className="page-tour-close" onClick={close} aria-label="Close page tour">&#215;</button></div>
        <div className="page-tour-progress" aria-label={`Step ${index + 1} of ${steps.length}`}>{steps.map((_, i) => <span key={i} className={i <= index ? 'is-filled' : ''} />)}</div>
        <h2>{steps[index][1]}</h2><p id="guide-description">{steps[index][2]}</p>
        {layout?.hint && <p className="tour-hint">{layout.hint}</p>}
        {!layout?.rect && section !== 'reader' && <p className="tour-hint">Nothing here just yet. Let's explore the next tip.</p>}
        <div className="page-tour-actions"><button className="page-tour-secondary" onClick={index > 0 ? () => { stepDirection.current = -1; setIndex(i => i - 1); } : close}>{index > 0 ? 'Back' : 'Not now'}</button><span className="page-tour-count">{index + 1} / {steps.length}</span><button className="page-tour-next" onClick={() => { stepDirection.current = 1; index === steps.length - 1 ? close() : setIndex(i => i + 1); }}>{index === steps.length - 1 ? 'Done' : 'Next'}<span aria-hidden="true"> &#8594;</span></button></div>
      </aside>
    </div>, document.fullscreenElement || document.body)}
  </>;
}
